/**
 * Comprehensive error handling and classification for authentication flows
 * Provides specific, actionable error messages for different failure types
 */

export interface AuthErrorClassification {
  type: AuthErrorType;
  message: string;
  actionableGuidance: string[];
  isRetryable: boolean;
  retryDelay?: number; // milliseconds
}

export type AuthErrorType = 
  | 'network_error'
  | 'rate_limited'
  | 'email_not_confirmed'
  | 'invalid_credentials'
  | 'account_exists'
  | 'email_delivery_issue'
  | 'weak_password'
  | 'invalid_email'
  | 'server_error'
  | 'token_expired'
  | 'unknown_error';

/**
 * Classify authentication errors and provide specific guidance
 */
export const classifyAuthError = (error: unknown): AuthErrorClassification => {
  const errorMessage = getErrorMessage(error);
  const errorCode = getErrorCode(error);
  const statusCode = getStatusCode(error);

  // Network and connectivity errors
  if (isNetworkError(errorMessage)) {
    return {
      type: 'network_error',
      message: 'Network connection failed',
      actionableGuidance: [
        'Check your internet connection',
        'Try connecting to a different network',
        'Disable VPN if you\'re using one',
        'Try again in a few moments'
      ],
      isRetryable: true,
      retryDelay: 3000
    };
  }

  // Rate limiting
  if (isRateLimited(errorMessage, statusCode)) {
    return {
      type: 'rate_limited',
      message: 'Too many attempts',
      actionableGuidance: [
        'Please wait a few minutes before trying again',
        'You\'ve made too many requests in a short time',
        'This is a security measure to protect your account'
      ],
      isRetryable: true,
      retryDelay: 60000 // 1 minute
    };
  }

  // Email not confirmed
  if (isEmailNotConfirmed(errorMessage, errorCode)) {
    return {
      type: 'email_not_confirmed',
      message: 'Email address not confirmed',
      actionableGuidance: [
        'Check your email inbox for a confirmation message',
        'Check your spam/junk folder',
        'Click the confirmation link in the email',
        'Use the "Resend confirmation" button if needed'
      ],
      isRetryable: false
    };
  }

  // Invalid credentials
  if (isInvalidCredentials(errorMessage)) {
    return {
      type: 'invalid_credentials',
      message: 'Invalid email or password',
      actionableGuidance: [
        'Double-check your email address for typos',
        'Make sure your password is correct',
        'Try using "Forgot password?" if you\'re unsure',
        'Ensure Caps Lock is not enabled'
      ],
      isRetryable: false
    };
  }

  // Account already exists
  if (isAccountExists(errorMessage)) {
    return {
      type: 'account_exists',
      message: 'Account already exists',
      actionableGuidance: [
        'An account with this email already exists',
        'Try signing in instead of creating a new account',
        'Use "Forgot password?" if you don\'t remember your password',
        'Contact support if you believe this is an error'
      ],
      isRetryable: false
    };
  }

  // Email delivery issues
  if (isEmailDeliveryIssue(errorMessage)) {
    return {
      type: 'email_delivery_issue',
      message: 'Email delivery failed',
      actionableGuidance: [
        'Check that your email address is correct',
        'Try using a different email address',
        'Check your email provider\'s spam settings',
        'Contact support if the problem persists'
      ],
      isRetryable: true,
      retryDelay: 5000
    };
  }

  // Weak password
  if (isWeakPassword(errorMessage)) {
    return {
      type: 'weak_password',
      message: 'Password does not meet requirements',
      actionableGuidance: [
        'Use at least 8 characters',
        'Include uppercase and lowercase letters',
        'Add at least one number',
        'Include a special character (!@#$%^&*)'
      ],
      isRetryable: false
    };
  }

  // Invalid email format
  if (isInvalidEmail(errorMessage)) {
    return {
      type: 'invalid_email',
      message: 'Invalid email address format',
      actionableGuidance: [
        'Check your email address for typos',
        'Make sure it includes @ and a domain (e.g., user@example.com)',
        'Remove any extra spaces',
        'Use a valid email format'
      ],
      isRetryable: false
    };
  }

  // Server errors
  if (isServerError(statusCode)) {
    return {
      type: 'server_error',
      message: 'Server temporarily unavailable',
      actionableGuidance: [
        'Our servers are experiencing issues',
        'Please try again in a few minutes',
        'Check our status page for updates',
        'Contact support if the problem persists'
      ],
      isRetryable: true,
      retryDelay: 10000
    };
  }

  // Token expired
  if (isTokenExpired(errorMessage)) {
    return {
      type: 'token_expired',
      message: 'Session expired',
      actionableGuidance: [
        'Your session has expired for security',
        'Please sign in again',
        'This helps protect your account'
      ],
      isRetryable: false
    };
  }

  // Unknown error fallback
  return {
    type: 'unknown_error',
    message: 'An unexpected error occurred',
    actionableGuidance: [
      'Please try again',
      'If the problem persists, contact support',
      'Include any error details when contacting support'
    ],
    isRetryable: true,
    retryDelay: 5000
  };
};

/**
 * Helper functions to identify specific error types
 */

const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message).toLowerCase();
  }
  return '';
};

const getErrorCode = (error: unknown): string => {
  if (error && typeof error === 'object' && 'code' in error) {
    return String(error.code).toLowerCase();
  }
  return '';
};

const getStatusCode = (error: unknown): number => {
  if (error && typeof error === 'object' && 'status' in error) {
    return Number(error.status) || 0;
  }
  return 0;
};

const isNetworkError = (message: string): boolean => {
  return message.includes('failed to fetch') ||
         message.includes('network error') ||
         message.includes('connection failed') ||
         message.includes('timeout') ||
         message.includes('fetch');
};

const isRateLimited = (message: string, status: number): boolean => {
  return status === 429 ||
         message.includes('rate limit') ||
         message.includes('too many requests') ||
         message.includes('rate exceeded');
};

const isEmailNotConfirmed = (message: string, code: string): boolean => {
  return message.includes('email not confirmed') ||
         code === 'email_not_confirmed' ||
         message.includes('confirm your email');
};

const isInvalidCredentials = (message: string): boolean => {
  return message.includes('invalid login credentials') ||
         message.includes('invalid credentials') ||
         message.includes('wrong password') ||
         message.includes('incorrect password');
};

const isAccountExists = (message: string): boolean => {
  return message.includes('user already registered') ||
         message.includes('email already exists') ||
         message.includes('account already exists') ||
         message.includes('already registered');
};

const isEmailDeliveryIssue = (message: string): boolean => {
  return message.includes('email delivery') ||
         message.includes('failed to send email') ||
         message.includes('email service') ||
         message.includes('smtp');
};

const isWeakPassword = (message: string): boolean => {
  return message.includes('password is too weak') ||
         message.includes('password does not meet') ||
         message.includes('password requirements') ||
         message.includes('weak password');
};

const isInvalidEmail = (message: string): boolean => {
  return message.includes('invalid email') ||
         message.includes('email format') ||
         message.includes('malformed email') ||
         message.includes('invalid email address');
};

const isServerError = (status: number): boolean => {
  return status >= 500 && status < 600;
};

const isTokenExpired = (message: string): boolean => {
  return message.includes('token expired') ||
         message.includes('session expired') ||
         message.includes('jwt expired') ||
         message.includes('token invalid');
};

/**
 * Format error for user display
 */
export const formatAuthError = (classification: AuthErrorClassification): {
  title: string;
  description: string;
  guidance: string[];
} => {
  return {
    title: classification.message,
    description: classification.actionableGuidance[0] || 'Please try again',
    guidance: classification.actionableGuidance
  };
};
